import React, {useEffect, useState} from "react";
import {useNavigate, useOutletContext, useParams, useSearchParams} from "react-router-dom";
import Button from "../../../../../components/Button";
import api from "../../../../../lib/api";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";
import {useBusiness} from "../../../../../context/BusinessContext";
import {CalendarDaysIcon, PlusIcon, SaveIcon, Trash2Icon} from "lucide-react";
import SectionHeading from "../../../../../components/SectionHeading";
import type {EventDayRequest, EventDayResponse} from "../../../../../types/event";

type OutletCtx = { eventId: string | null };

type DayRow = EventDayRequest & { _key: string };

function isoToLocalInput(iso?: string | null): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function newRow(position: number): DayRow {
    return {_key: crypto.randomUUID(), name: "", startTime: "", endTime: "", position};
}

export function EventDaysStep({mode}: { mode: "create" | "edit" }) {
    const {activeBusiness} = useBusiness();
    const businessId = activeBusiness?.id ?? null;

    const navigate = useNavigate();
    const params = useParams();
    const [sp] = useSearchParams();
    const outlet = useOutletContext<OutletCtx>();

    const eventId =
        mode === "edit"
            ? (params.eventId ?? outlet.eventId ?? null)
            : (outlet.eventId ?? sp.get("eventId") ?? null);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [days, setDays] = useState<DayRow[]>([]);

    useEffect(() => {
        if (!businessId || !eventId) return;
        setLoading(true);
        (async () => {
            try {
                const {data} = await api.get(
                    `/api/v1/business/${businessId}/events/${eventId}/days`
                );
                setDays(((data ?? []) as EventDayResponse[]).map((d) => ({
                    ...d,
                    startTime: isoToLocalInput(d.startTime),
                    endTime: isoToLocalInput(d.endTime),
                    _key: d.id
                })));
            } catch (e) {
                notify.error(getErrMsg(e, "Failed to load event days"));
            } finally {
                setLoading(false);
            }
        })();
    }, [businessId, eventId]);

    const generate = async () => {
        if (!businessId || !eventId) return;
        if (days.length > 0 && !confirm("This will replace the current days with auto-generated ones. Continue?")) return;
        setGenerating(true);
        try {
            const {data} = await api.post(
                `/api/v1/business/${businessId}/events/${eventId}/days/generate`
            );
            setDays(((data ?? []) as EventDayResponse[]).map((d) => ({
                ...d,
                startTime: isoToLocalInput(d.startTime),
                endTime: isoToLocalInput(d.endTime),
                _key: d.id
            })));
            notify.success("Days generated");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to generate days"));
        } finally {
            setGenerating(false);
        }
    };

    const save = async () => {
        if (!businessId || !eventId) return;
        setSaving(true);
        try {
            const payload: EventDayRequest[] = days.map(({_key, ...d}, i) => ({...d, position: i}));
            const {data} = await api.patch(
                `/api/v1/business/${businessId}/events/${eventId}/days`,
                payload
            );
            setDays(((data ?? []) as EventDayResponse[]).map((d) => ({
                ...d,
                startTime: isoToLocalInput(d.startTime),
                endTime: isoToLocalInput(d.endTime),
                _key: d.id
            })));
            notify.success("Event days saved");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to save event days"));
        } finally {
            setSaving(false);
        }
    };

    const deleteDay = async (row: DayRow) => {
        if (!businessId || !eventId) return;
        if (row.id) {
            try {
                await api.delete(`/api/v1/business/${businessId}/events/${eventId}/days/${row.id}`);
            } catch (e) {
                notify.error(getErrMsg(e, "Failed to delete day"));
                return;
            }
        }
        setDays((prev) => prev.filter((d) => d._key !== row._key));
    };

    const updateRow = (key: string, patch: Partial<DayRow>) => {
        setDays((prev) => prev.map((d) => (d._key === key ? {...d, ...patch} : d)));
    };

    const addRow = () => {
        setDays((prev) => [...prev, newRow(prev.length)]);
    };

    const goNext = () => {
        if (!eventId) return;
        if (mode === "edit") navigate(`/business/events/${encodeURIComponent(eventId)}/edit/images`);
        else navigate(`/business/events/new/images?eventId=${encodeURIComponent(eventId)}`);
    };

    if (!eventId) {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
                Please complete <span className="font-medium">Event Details</span> first.
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <SectionHeading
                title={mode === "edit" ? "Edit Event Days" : "Set Up Event Days"}
                description="Define individual days for your event. You can generate days automatically from the event dates or add them manually."
                actions={
                    <>
                        <Button type="button" variant="secondary" size="md"
                                onClick={generate}
                                disabled={generating || saving}>
                            <span className="inline-flex items-center gap-2">
                                <CalendarDaysIcon className="size-4"/>
                                {generating ? "Generating…" : "Generate Days"}
                            </span>
                        </Button>
                        <Button type="button" variant="primary" size="md"
                                onClick={save}
                                disabled={saving || generating}>
                            {saving ? "Saving…" :
                                <span className="inline-flex items-center gap-2"><SaveIcon
                                    className="size-4"/>Save</span>}
                        </Button>
                    </>
                }
            />

            {loading ? (
                <div className="py-10 text-sm text-gray-500">Loading event days…</div>
            ) : (
                <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                    {days.length === 0 ? (
                        <div className="px-6 py-10 text-center text-sm text-gray-500">
                            No days added yet. Use <span className="font-medium">Generate Days</span> or add one
                            manually.
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {days.map((row, i) => (
                                <div key={row._key}
                                     className="grid grid-cols-1 gap-3 px-6 py-4 sm:grid-cols-[1fr_1fr_1fr_auto]">
                                    <div>
                                        <label className="mb-1 block text-xs font-medium text-gray-700">Day name</label>
                                        <input
                                            type="text"
                                            value={row.name}
                                            placeholder={`Day ${i + 1}`}
                                            onChange={(e) => updateRow(row._key, {name: e.target.value})}
                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-orange-600 focus:outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-xs font-medium text-gray-700">Start
                                            time</label>
                                        <input
                                            type="datetime-local"
                                            value={row.startTime}
                                            onChange={(e) => updateRow(row._key, {startTime: e.target.value})}
                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-orange-600 focus:outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-xs font-medium text-gray-700">End time</label>
                                        <input
                                            type="datetime-local"
                                            value={row.endTime}
                                            onChange={(e) => updateRow(row._key, {endTime: e.target.value})}
                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-orange-600 focus:outline-none"
                                        />
                                    </div>
                                    <div className="flex items-end">
                                        <button
                                            type="button"
                                            onClick={() => deleteDay(row)}
                                            className="rounded-md p-2 text-gray-500 hover:bg-red-50 hover:text-red-600"
                                            aria-label="Delete day"
                                        >
                                            <Trash2Icon className="size-4"/>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="border-t border-gray-100 px-6 py-3">
                        <button
                            type="button"
                            onClick={addRow}
                            className="inline-flex items-center gap-2 text-sm font-medium text-orange-700 hover:text-orange-800"
                        >
                            <PlusIcon className="size-4"/>
                            Add day
                        </button>
                    </div>
                </div>
            )}

            <div className="flex items-center justify-end gap-3">
                <Button type="button" variant="secondary" size="md"
                        onClick={() => navigate("/business/events")}>
                    Exit
                </Button>
                <Button type="button" variant="primary" size="md" onClick={goNext} disabled={saving}>
                    Continue to Images
                </Button>
            </div>
        </div>
    );
}
